import type { HTMLAttributes, ReactNode, TdHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

/**
 * Sheet — the document-style table primitive. A "paper" surface whose tables
 * are as tall as their content: no sorting, no sticky headers, no internal
 * scroll, no maxHeight. The page scrolls; the sheet never does. Built for
 * register/tracker surfaces that must read (and print) like a document, not
 * a dashboard.
 */

export interface SheetProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

/** The paper: resting surface (border, no shadow), generous padding. */
export default function Sheet({ children, className, ...props }: SheetProps) {
  return (
    <section
      className={cn(
        'w-full rounded-[var(--radius-surface)] border border-border bg-surface-card p-8',
        className,
      )}
      {...props}
    >
      {children}
    </section>
  );
}

export interface SheetHeadingProps {
  title: ReactNode;
  /** Muted caption line under the title — dates, rounds, inline controls. */
  meta?: ReactNode;
  className?: string;
}

export function SheetHeading({ title, meta, className }: SheetHeadingProps) {
  return (
    <header className={cn('mb-6', className)}>
      <h2 className="t-card text-green-900">{title}</h2>
      {meta !== undefined && <div className="t-caption mt-1 text-ink-soft">{meta}</div>}
    </header>
  );
}

export interface SheetSectionProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode;
  /** Right-hand slot on the section heading row (quiet links, icon buttons). */
  end?: ReactNode;
  children: ReactNode;
}

export function SheetSection({ title, end, children, className, ...props }: SheetSectionProps) {
  return (
    <section className={cn('mt-8 first:mt-0', className)} {...props}>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="t-label text-ink-soft">{title}</h3>
        {end !== undefined && <div className="flex shrink-0 items-center gap-2">{end}</div>}
      </div>
      {children}
    </section>
  );
}

export interface SheetColumn {
  key: string;
  label: ReactNode;
  /** Tailwind width class (w-24, w-56, ...). Omit for the flex column. */
  width?: string;
}

export interface SheetTableProps {
  /** sr-only <caption> for screen readers. */
  caption: string;
  columns: SheetColumn[];
  /** Rows: <SheetRow> children. */
  children: ReactNode;
  className?: string;
}

export function SheetTable({ caption, columns, children, className }: SheetTableProps) {
  return (
    <table className={cn('w-full table-fixed border-collapse', className)}>
      <caption className="sr-only">{caption}</caption>
      <colgroup>
        {columns.map((c) => (
          <col key={c.key} className={c.width} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.key}
              scope="col"
              className="t-eyebrow border-b border-border py-2 pr-2 text-left align-bottom"
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

export type SheetRowProps = HTMLAttributes<HTMLTableRowElement>;

export function SheetRow({ className, ...props }: SheetRowProps) {
  return (
    <tr
      className={cn(
        props.onClick && 'cursor-pointer transition-colors hover:bg-cream-50',
        className,
      )}
      {...props}
    />
  );
}

export type SheetCellTone = 'muted' | 'danger' | 'ident';

const CELL_TONE: Record<SheetCellTone, string> = {
  muted: 'text-ink-soft',
  danger: 'text-danger',
  ident: 't-ident',
};

export interface SheetCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  tone?: SheetCellTone;
}

export function SheetCell({ tone, className, ...props }: SheetCellProps) {
  return (
    <td
      className={cn(
        'border-b border-border-subtle py-2 pr-2 align-top text-base text-ink',
        tone && CELL_TONE[tone],
        className,
      )}
      {...props}
    />
  );
}
